'use client'

import { XIcon } from 'lucide-react'
import { Dialog as SheetPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from './cn'

const Sheet = ({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>): React.ReactElement => {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

const SheetTrigger = ({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>): React.ReactElement => {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

const SheetClose = ({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>): React.ReactElement => {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

const SheetPortal = ({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>): React.ReactElement => {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

const SheetOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>): React.ReactElement => {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'snplay:fixed snplay:inset-0 snplay:z-[100] snplay:backdrop-blur-sm',
        'snplay:data-[state=closed]:animate-out snplay:data-[state=closed]:fade-out-0',
        'snplay:data-[state=open]:animate-in snplay:data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  )
}

type SheetContentProps = React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left'
  showCloseButton?: boolean
}

const SheetContent = ({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  ...props
}: SheetContentProps): React.ReactElement => {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          'snplay:fixed snplay:z-[250] snplay:flex snplay:flex-col',
          'snplay:gap-4 snplay:bg-clip-padding snplay:text-sm snplay:shadow-lg',
          'snplay:bg-background',
          'snplay:duration-200 snplay:ease-in-out',
          'snplay:data-[state=closed]:animate-out snplay:data-[state=closed]:fade-out-0',
          'snplay:data-[state=open]:animate-in snplay:data-[state=open]:fade-in-0',
          'snplay:data-[side=bottom]:inset-x-0 snplay:data-[side=bottom]:bottom-0',
          'snplay:data-[side=bottom]:h-auto',
          'snplay:data-[side=left]:inset-y-0 snplay:data-[side=left]:left-0',
          'snplay:data-[side=left]:h-full',
          'snplay:data-[side=left]:w-3/4 snplay:max-xs:data-[side=right]:w-[90%]',
          'snplay:data-[side=left]:sm:max-w-lg',
          'snplay:data-[side=right]:inset-y-0 snplay:data-[side=right]:right-0',
          'snplay:data-[side=right]:h-full',
          'snplay:data-[side=right]:w-3/4 snplay:data-[side=right]:sm:max-w-lg',
          'snplay:data-[side=top]:inset-x-0 snplay:data-[side=top]:top-0',
          'snplay:data-[side=top]:h-auto',
          'snplay:data-[side=bottom]:data-[state=closed]:slide-out-to-bottom-10',
          'snplay:data-[side=bottom]:data-[state=open]:slide-in-from-bottom-10',
          'snplay:data-[side=left]:data-[state=closed]:slide-out-to-left-10',
          'snplay:data-[side=left]:data-[state=open]:slide-in-from-left-10',
          'snplay:data-[side=right]:data-[state=closed]:slide-out-to-right-10',
          'snplay:data-[side=right]:data-[state=open]:slide-in-from-right-10',
          'snplay:data-[side=top]:data-[state=closed]:slide-out-to-top-10',
          'snplay:data-[side=top]:data-[state=open]:slide-in-from-top-10',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close data-slot="sheet-close" asChild>
            <button
              type="button"
              className={cn(
                'snplay-can-focus snplay:absolute snplay:right-3 snplay:top-3',
                'snplay:flex snplay:size-8 snplay:cursor-pointer',
                'snplay:items-center snplay:justify-center snplay:rounded-lg snplay:transition',
                'snplay:focus-visible:outline-2 snplay:focus-visible:outline-offset-2',
              )}
            >
              <XIcon className="snplay:size-4" />
              <span className="snplay:sr-only">Close</span>
            </button>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

const SheetHeader = ({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement => {
  return (
    <div
      data-slot="sheet-header"
      className={cn('snplay:flex snplay:flex-col snplay:gap-0.5 snplay:p-4', className)}
      {...props}
    />
  )
}

const SheetFooter = ({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement => {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        'snplay:mt-auto snplay:flex snplay:flex-col',
        'snplay:gap-2 snplay:p-4',
        className,
      )}
      {...props}
    />
  )
}

const SheetTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>): React.ReactElement => {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('snplay:text-base snplay:font-medium', className)}
      {...props}
    />
  )
}

const SheetDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>): React.ReactElement => {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('snplay:text-sm', className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
}
